import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import SiteShell from '@/components/feature/SiteShell';
import Breadcrumb from '@/components/base/Breadcrumb';
import { usePageMeta } from '@/hooks/usePageMeta';

const FORM_ID = 'db9lmr8kk00l6eltrt9g';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/db9lmr8kk00l6eltrt9g';

const PRINCIPLES = [
  {
    icon: 'ri-focus-3-line',
    title: '内容先行',
    desc: '先讲清楚一部作品是什么、谁拍的、什么时候更新，再谈推荐。信息完整比标题刺激更重要。',
  },
  {
    icon: 'ri-layout-grid-line',
    title: '结构清晰',
    desc: '分类、筛选、排序与面包屑形成稳定的浏览路径，随时知道自己在哪、下一步去哪。',
  },
  {
    icon: 'ri-smartphone-line',
    title: '两端一致',
    desc: '桌面与移动端共享同一套内容结构，手机上不横向滚动，按钮大小适合手指点击。',
  },
];

const FAQS = [
  {
    q: '网站上的影视作品是真实存在的吗？',
    a: '当前站内所有影视条目、评分、主创与资讯内容均为虚构示例数据，用于展示影视内容平台的信息组织方式，不对应任何真实作品。',
  },
  {
    q: '详情页的视频可以真正播放吗？',
    a: '不可以。详情页的播放区域是纯前端交互演示，包含播放、暂停、进度拖动、静音与全屏等控件的操作反馈，但没有真实视频源。真实播放能力标记为“待接入”。',
  },
  {
    q: '为什么没有登录、会员或支付功能？',
    a: '当前版本聚焦于内容浏览与信息获取，未包含账号体系与付费能力。收藏、评论入口仅作展示或本地临时状态，不会上传任何数据。',
  },
];

export default function About() {
  usePageMeta({
    title: '关于我们 - 黄牛视频影视内容平台',
    description:
      '了解黄牛视频的定位、内容准则与联系方式。黄牛视频专注电影、电视剧与综艺内容的资讯整理、分类筛选与播放演示。',
    keywords: '关于黄牛视频,影视内容平台,联系我们,版权声明,用户须知',
  });

  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('contact_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'contact_alt') return;
      const text = String(value).trim();
      if (key === 'message' && text.length > 500) {
        payload.set(key, text.slice(0, 500));
        return;
      }
      payload.set(key, text);
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        '';
      const isSuccess = response.ok && parsed?.code === 'OK';

      if (isSuccess) {
        setStatus('success');
        form.reset();
        return;
      }

      setStatus('error');
      setFormError(
        serverMessage
          ? `提交未成功：${serverMessage}`
          : '提交未成功，请稍后再试，或直接通过邮箱联系我们。',
      );
    } catch {
      setStatus('error');
      setFormError('网络异常，留言没有发送成功，请稍后再试或使用邮箱联系我们。');
    }
  };

  return (
    <SiteShell>
      <div className="w-full px-4 pb-2 pt-5 md:px-8 md:pt-7">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
      </div>

      <section className="w-full px-4 pb-8 pt-4 md:px-8 md:pb-10">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:items-center">
          <div>
            <span className="font-label text-[10px] uppercase tracking-[0.24em] text-accent-600">
              About Huangniu Video
            </span>
            <h1 className="mt-3 font-heading text-3xl font-extrabold leading-tight tracking-tight text-foreground-950 md:text-4xl">
              黄牛视频，<br className="hidden sm:block" />
              把影视内容讲清楚的地方
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-foreground-600">
              我们是一个面向电影、电视剧与综艺爱好者的影视内容平台。这里没有夸张的标题党，只有整理好的作品信息、清晰的分类结构与及时的行业动态，帮你更快决定“今晚看什么”。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/category/movie"
                className="whitespace-nowrap rounded-md bg-primary-900 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800"
              >
                浏览影视内容
              </Link>
              <Link
                to="/news"
                className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100"
              >
                查看影视资讯
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {[
              { value: '24', label: '精选影视条目', unit: '部' },
              { value: '10', label: '资讯报道', unit: '篇' },
              { value: '3', label: '内容频道', unit: '个' },
              { value: '2026', label: '站点上线', unit: '年' },
            ].map((item) => (
              <div key={item.label} className="rounded-lg border border-background-200 bg-background-100/60 p-4">
                <p className="font-heading text-2xl font-extrabold text-foreground-950">
                  {item.value}
                  <span className="ml-1 font-label text-[11px] font-medium text-foreground-400">
                    {item.unit}
                  </span>
                </p>
                <p className="mt-1 text-[11px] text-foreground-500">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full border-y border-background-200 bg-background-100/60 px-4 py-10 md:px-8 md:py-12">
        <h2 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
          我们在坚持什么
        </h2>
        <p className="mt-2 max-w-2xl text-sm text-foreground-500">
          内容型网站的价值不在于页面有多少，而在于用户能否在三次点击内找到需要的信息。
        </p>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {PRINCIPLES.map((item, index) => (
            <div key={item.title} className="rounded-lg bg-background-50 p-5">
              <div className="flex items-center gap-3">
                <span
                  className={`flex h-10 w-10 items-center justify-center rounded-md ${
                    index === 1
                      ? 'bg-secondary-100 text-secondary-800'
                      : index === 2
                        ? 'bg-accent-100 text-accent-800'
                        : 'bg-primary-100 text-primary-800'
                  }`}
                >
                  <i className={`${item.icon} text-xl`} />
                </span>
                <h3 className="font-heading text-sm font-bold text-foreground-950">{item.title}</h3>
              </div>
              <p className="mt-3 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] lg:gap-12">
          <div>
            <h2 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
              联系方式与留言
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              内容纠错、合作咨询或功能建议，都可以通过下面的表单提交，也可以直接发邮件给我们。
            </p>

            <dl className="mt-5 space-y-3">
              {[
                { icon: 'ri-mail-line', label: '联系邮箱', value: 'hello@huangniu-video.example' },
                { icon: 'ri-map-pin-line', label: '办公地址', value: '北京市朝阳区望京东路 18 号 影视创意园 B 座 7 层' },
                { icon: 'ri-time-line', label: '回复时间', value: '工作日 10:00 - 18:00，通常在 2 个工作日内回复' },
              ].map((row) => (
                <div
                  key={row.label}
                  className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-3.5"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                    <i className={`${row.icon} text-sm`} />
                  </span>
                  <div>
                    <dt className="text-[11px] text-foreground-400">{row.label}</dt>
                    <dd className="mt-0.5 text-sm text-foreground-800">{row.value}</dd>
                  </div>
                </div>
              ))}
            </dl>

            <div className="mt-6">
              <h3 className="font-heading text-sm font-bold text-foreground-950">常见问题</h3>
              <div className="mt-3 divide-y divide-background-200 border-y border-background-200">
                {FAQS.map((faq, index) => (
                  <div key={faq.q}>
                    <button
                      type="button"
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      aria-expanded={openFaq === index}
                      className="flex w-full cursor-pointer items-center justify-between gap-3 py-3 text-left"
                    >
                      <span className="text-sm text-foreground-800">{faq.q}</span>
                      <i
                        className={`${
                          openFaq === index ? 'ri-subtract-line' : 'ri-add-line'
                        } shrink-0 text-foreground-400`}
                      />
                    </button>
                    {openFaq === index && (
                      <p className="animate-fade-in pb-3 text-xs leading-relaxed text-foreground-500">
                        {faq.a}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100/50 p-4 md:p-6">
            <h2 className="font-heading text-base font-bold text-foreground-950">给我们留言</h2>
            <p className="mt-1.5 text-xs text-foreground-500">
              带 <span className="text-accent-600">*</span> 的为必填项，留言会提交至站内表单记录。
            </p>

            {status === 'success' ? (
              <div className="mt-5 rounded-lg border border-secondary-200 bg-secondary-50 px-4 py-8 text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-secondary-100 text-secondary-800">
                  <i className="ri-check-line text-2xl" />
                </span>
                <p className="mt-3 font-heading text-sm font-bold text-secondary-900">留言已提交</p>
                <p className="mt-2 text-xs leading-relaxed text-secondary-800/80">
                  感谢你的反馈，我们会在工作日尽快查看。你也可以继续浏览影视内容。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus('idle')}
                  className="mt-4 cursor-pointer rounded-md border border-secondary-300 px-4 py-2 text-xs text-secondary-900"
                >
                  再写一条
                </button>
              </div>
            ) : (
              <form
                id={FORM_ID}
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-4 space-y-4"
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="about-name" className="mb-1.5 block text-xs text-foreground-600">
                      称呼 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="about-name"
                      name="name"
                      type="text"
                      required
                      placeholder="怎么称呼你"
                      className="field-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="mb-1.5 block text-xs text-foreground-600">
                      邮箱 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="about-email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于接收回复"
                      className="field-input"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="about-topic" className="mb-1.5 block text-xs text-foreground-600">
                    留言类型
                  </label>
                  <select id="about-topic" name="topic" className="field-input">
                    <option value="内容纠错">内容纠错</option>
                    <option value="合作咨询">合作咨询</option>
                    <option value="功能建议">功能建议</option>
                    <option value="版权相关">版权相关</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="about-message" className="mb-1.5 block text-xs text-foreground-600">
                    留言内容 <span className="text-accent-600">*</span>
                  </label>
                  <textarea
                    id="about-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请简要描述你的问题或建议（500 字以内）"
                    className="field-input resize-none"
                  />
                </div>

                <div className="form-extra-note" aria-hidden="true">
                  <label htmlFor="about-contact-alt">备用联系方式</label>
                  <input
                    id="about-contact-alt"
                    name="contact_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                {status === 'error' && formError && (
                  <p className="rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-xs leading-relaxed text-accent-900">
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-900 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === 'submitting' ? '提交中…' : '提交留言'}
                </button>
                <p className="text-[11px] leading-relaxed text-foreground-400">
                  提交即表示你已阅读下方的用户须知。我们仅使用你填写的信息回复本次留言。
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100/70 px-4 py-10 md:px-8 md:py-12">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="rounded-lg bg-background-50 p-5">
            <h2 className="font-heading text-sm font-bold text-foreground-950">版权声明</h2>
            <p className="mt-2 text-xs leading-6 text-foreground-600">
              本站所有影视条目名称、人物信息、评分与资讯内容均为虚构示例，不对应任何真实作品或真实人物，仅用于产品演示。站内图片为模型生成的示意素材，不涉及真实影视版权素材。如未来接入真实内容，将另行标注来源并遵循相应授权。
            </p>
          </div>
          <div className="rounded-lg bg-background-50 p-5">
            <h2 className="font-heading text-sm font-bold text-foreground-950">用户须知</h2>
            <ul className="mt-2 space-y-1.5 text-xs leading-6 text-foreground-600">
              <li>· 详情页播放区域为前端交互演示，不提供真实视频源，相关能力标记为“待接入”。</li>
              <li>· 站内不设账号、会员与支付功能，收藏与分享仅在本地生效。</li>
              <li>· 搜索、筛选与排序结果均由浏览器端示例数据计算，不代表真实数据库结果。</li>
              <li>· 留言信息仅用于回复本次咨询，不会用于其他用途。</li>
            </ul>
            <p className="mt-3 text-[11px] text-foreground-400">
              © 2026 黄牛视频 · 京ICP备2026000000号
            </p>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}